import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import SectionCard from "../../components/SectionCard";
import useAccess from "../../hooks/useAccess";
import "../../styles/ReferenceCheckAdminPage.css";

function normalizeQuestions(rows = []) {
  return rows.map((row, index) => ({
    ...row,
    rcq_sort_order: Number(row.rcq_sort_order || index + 1),
    rcq_type: row.rcq_type === "radio" ? "select" : row.rcq_type,
    rcq_options: Array.isArray(row.rcq_options) ? row.rcq_options : [],
    rcq_required: row.rcq_required !== false,
  }));
}

const ReferenceCheckAdminPage = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { hasPermission, currentUser } = useAccess();

  const authHeader = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const [settings, setSettings] = useState({
    sender_name: "New Way HR Portal",
    sender_email: "",
    expiry_days: 7,
  });
  const [questions, setQuestions] = useState([]);
  const [summary, setSummary] = useState({
    counts: {
      total_sent: 0,
      completed_count: 0,
      pending_count: 0,
      expired_count: 0,
    },
    records: [],
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem("referenceCheckAdminTab") || "settings"
  );

  const roleIds = useMemo(() => {
    try {
      const parsed =
        typeof currentUser?.emp_roles === "string"
          ? JSON.parse(currentUser.emp_roles)
          : currentUser?.emp_roles;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  }, [currentUser]);

  const permissions = useMemo(
    () => ({
      view:
        hasPermission("AccountSetting", "Reference Check", "view") ||
        hasPermission("AccountSetting", "Reference Check", "edit") ||
        hasPermission("AccountSetting", "Reference Check", "delete"),
      edit: hasPermission("AccountSetting", "Reference Check", "edit"),
      delete: hasPermission("AccountSetting", "Reference Check", "delete"),
      isSuperAdmin: roleIds.includes(17),
    }),
    [hasPermission, roleIds]
  );

  const filteredRecords = useMemo(() => {
    const records = summary.records || [];
    const query = searchTerm.trim().toLowerCase();

    if (!query) return records;

    return records.filter((record) => {
      const haystack = [
        record.userid_no,
        record.applicant_name,
        record.wh_name,
        record.rc_company_email,
        record.rc_status,
        record.rc_sent_at ? String(record.rc_sent_at).slice(0, 10) : "",
        record.rc_expires_at ? String(record.rc_expires_at).slice(0, 10) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [searchTerm, summary.records]);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, questionsRes, summaryRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/reference-check/settings`, {
          headers: authHeader,
        }),
        axios.get(`${BASE_URL}/api/reference-check/questions`, {
          headers: authHeader,
        }),
        axios.get(`${BASE_URL}/api/reference-check/summary`, {
          headers: authHeader,
        }),
      ]);

      setSettings((prev) => ({
        ...prev,
        ...(settingsRes.data || {}),
      }));
      setQuestions(normalizeQuestions(questionsRes.data || []));
      setSummary(
        summaryRes.data || {
          counts: {
            total_sent: 0,
            completed_count: 0,
            pending_count: 0,
            expired_count: 0,
          },
          records: [],
        }
      );
    } catch (err) {
      console.error("Failed to load reference check admin data:", err);
      setMessage("Failed to load reference check data.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, authHeader]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const addQuestion = () => {
    setEditingIndex(null);
    setShowQuestionModal(true);
  };

  const editQuestion = (index) => {
    setEditingIndex(index);
    setShowQuestionModal(true);
  };

  const removeQuestion = (index) => {
    setQuestions((prev) =>
      prev
        .filter((_, qIndex) => qIndex !== index)
        .map((question, qIndex) => ({
          ...question,
          rcq_sort_order: qIndex + 1,
        }))
    );
  };

  const saveSettings = async () => {
    try {
      setMessage("");
      await axios.post(`${BASE_URL}/api/reference-check/settings`, settings, {
        headers: authHeader,
      });
      setMessage("Reference check settings saved.");
      loadAll();
    } catch (err) {
      console.error("Failed to save settings:", err);
      setMessage("Failed to save settings.");
    }
  };

  const saveQuestions = async () => {
    try {
      setMessage("");
      await axios.post(
        `${BASE_URL}/api/reference-check/questions`,
        {
          questions: questions.map((question, index) => ({
            ...question,
            rcq_sort_order: index + 1,
          })),
        },
        { headers: authHeader }
      );
      setMessage("Reference check questions saved.");
      loadAll();
    } catch (err) {
      console.error("Failed to save questions:", err);
      setMessage("Failed to save questions.");
    }
  };

  const handleCancelReferenceCheck = async (checkId) => {
    if (!permissions.edit) return;
    if (!window.confirm("Cancel this reference check so it can be sent again?")) {
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/reference-check/cancel/${checkId}`, {
        headers: authHeader,
      });
      setMessage("Reference check cancelled.");
      loadAll();
    } catch (err) {
      console.error("Failed to cancel reference check:", err);
      setMessage(
        err.response?.data?.message || "Failed to cancel reference check."
      );
    }
  };

  const handleDeleteReferenceCheck = async (checkId) => {
    if (!permissions.delete || !permissions.isSuperAdmin) return;
    if (
      !window.confirm(
        "Delete this reference check record permanently? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/reference-check/${checkId}`, {
        headers: authHeader,
      });
      setMessage("Reference check deleted.");
      loadAll();
    } catch (err) {
      console.error("Failed to delete reference check:", err);
      setMessage(
        err.response?.data?.message || "Failed to delete reference check."
      );
    }
  };

  const handleSaveQuestion = (questionPayload) => {
    setQuestions((prev) => {
      if (editingIndex === null || editingIndex === undefined) {
        return [
          ...prev,
          {
            ...questionPayload,
            rcq_sort_order: prev.length + 1,
          },
        ];
      }

      return prev.map((question, index) =>
        index === editingIndex
          ? {
              ...question,
              ...questionPayload,
            }
          : question
      );
    });

    setShowQuestionModal(false);
    setEditingIndex(null);
  };

  return (
    <div>
      <SectionCard title="Reference Check">
        <div className="d-flex gap-2 mb-4 flex-wrap">
          <button
            className={`btn ${
              activeTab === "settings" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => {
              setActiveTab("settings");
              localStorage.setItem("referenceCheckAdminTab", "settings");
            }}
          >
            Settings & Summary
          </button>
          <button
            className={`btn ${
              activeTab === "questions" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => {
              setActiveTab("questions");
              localStorage.setItem("referenceCheckAdminTab", "questions");
            }}
          >
            Reference Check Questions
          </button>
        </div>

        {activeTab === "settings" && (
          <>
            <h4 className="mb-4">Reference Check Settings</h4>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Sender Name</label>
                <input
                  className="form-control"
                  value={settings.sender_name || ""}
                  disabled={!permissions.edit}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      sender_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Send From Email</label>
                <input
                  className="form-control"
                  value={settings.sender_email || ""}
                  disabled={!permissions.edit}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      sender_email: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Expiry Days</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={settings.expiry_days || 7}
                  disabled={!permissions.edit}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      expiry_days: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {permissions.edit && (
              <div className="d-flex gap-2 mt-3 mb-5">
                <button className="btn btn-primary" onClick={saveSettings}>
                  Save Settings
                </button>
              </div>
            )}

            <h4 className="mb-4">Reference Check Summary</h4>
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="border rounded p-3 bg-light h-100">
                  <div className="text-muted">Emails Sent</div>
                  <div className="fs-3 fw-bold">
                    {summary.counts.total_sent || 0}
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 bg-light h-100">
                  <div className="text-muted">Completed</div>
                  <div className="fs-3 fw-bold text-success">
                    {summary.counts.completed_count || 0}
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 bg-light h-100">
                  <div className="text-muted">Waiting</div>
                  <div className="fs-3 fw-bold text-warning">
                    {summary.counts.pending_count || 0}
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 bg-light h-100">
                  <div className="text-muted">Expired</div>
                  <div className="fs-3 fw-bold text-danger">
                    {summary.counts.expired_count || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="reference-check-admin-searchbar mb-3">
              <div className="reference-check-admin-searchcopy">
                <div className="reference-check-admin-searchkicker">
                  Search Reference Checks
                </div>
                <div className="reference-check-admin-searchhint">
                  Search by applicant, company, email, status, or user ID.
                </div>
              </div>
              <div className="reference-check-admin-searchcontrols">
                <input
                  type="text"
                  className="form-control reference-check-admin-searchinput"
                  placeholder="Search reference checks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="reference-check-admin-clearbtn"
                    onClick={() => setSearchTerm("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="reference-check-admin-searchcount mb-3">
              Showing {filteredRecords.length} of {(summary.records || []).length}{" "}
              reference check{filteredRecords.length === 1 ? "" : "s"}
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-striped align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>User ID</th>
                    <th>Applicant</th>
                    <th>Company</th>
                    <th>Company Email</th>
                    <th>Status</th>
                    <th>Sent</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.rc_id}>
                      <td>{record.userid_no || "-"}</td>
                      <td>{record.applicant_name || "-"}</td>
                      <td>{record.wh_name || "-"}</td>
                      <td>{record.rc_company_email || "-"}</td>
                      <td className="text-capitalize">
                        {record.rc_status || "-"}
                      </td>
                      <td>
                        {record.rc_sent_at
                          ? String(record.rc_sent_at).slice(0, 10)
                          : "-"}
                      </td>
                      <td>
                        {record.rc_expires_at
                          ? String(record.rc_expires_at).slice(0, 10)
                          : "-"}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              navigate(`/edit-workhistory/${record.wh_userid}`)
                            }
                          >
                            Open Work History
                          </button>
                          {permissions.edit && record.rc_status === "pending" && (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() =>
                                handleCancelReferenceCheck(record.rc_id)
                              }
                            >
                              Cancel
                            </button>
                          )}
                          {record.rc_status === "completed" && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() =>
                                navigate(
                                  `/reference-check-results/${record.rc_id}`
                                )
                              }
                            >
                              View Result
                            </button>
                          )}
                          {permissions.delete && permissions.isSuperAdmin && (
                            <button
                              type="button"
                              className="reference-check-admin-iconbtn"
                              onClick={() =>
                                handleDeleteReferenceCheck(record.rc_id)
                              }
                              title="Delete reference check"
                              aria-label="Delete reference check"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && !(summary.records || []).length && (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No reference checks yet.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    (summary.records || []).length > 0 &&
                    filteredRecords.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center text-muted py-4">
                          No reference checks match your search.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "questions" && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
              <h4 className="m-0">Reference Check Questions</h4>
              <div className="d-flex gap-2">
                {permissions.edit && (
                  <>
                    <button className="btn btn-outline-primary" onClick={addQuestion}>
                      Add Question
                    </button>
                    <button className="btn btn-primary" onClick={saveQuestions}>
                      Save Questions
                    </button>
                  </>
                )}
              </div>
            </div>

            {questions.map((question, index) => (
              <div
                key={question.rcq_id || `new-${index}`}
                className="border rounded p-3 mb-3 bg-light"
              >
                <div className="row g-3">
                  <div className="col-md-7">
                    <div className="fw-semibold">{question.rcq_question}</div>
                    {question.rcq_description && (
                      <div className="text-muted small mt-1">
                        {question.rcq_description}
                      </div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <div className="small text-muted">Type</div>
                    <div>{getTypeLabel(question.rcq_type)}</div>
                  </div>
                  <div className="col-md-2">
                    <div className="small text-muted">Required</div>
                    <div>{question.rcq_required ? "Yes" : "No"}</div>
                  </div>
                  {(question.rcq_type === "select" ||
                    question.rcq_type === "multiselect") && (
                    <div className="col-12">
                      <div className="small text-muted mb-1">Answers</div>
                      <div>{(question.rcq_options || []).join(", ") || "-"}</div>
                    </div>
                  )}
                </div>
                {permissions.edit && (
                  <div className="mt-3 d-flex gap-2">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => editQuestion(index)}
                    >
                      Edit Question
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeQuestion(index)}
                    >
                      Remove Question
                    </button>
                  </div>
                )}
              </div>
            ))}

            {!questions.length && !loading && (
              <div className="text-muted">No reference check questions yet.</div>
            )}
          </>
        )}

        {message && <div className="mt-3">{message}</div>}
      </SectionCard>

      <ReferenceQuestionEditor
        show={showQuestionModal}
        onClose={() => {
          setShowQuestionModal(false);
          setEditingIndex(null);
        }}
        editingQuestion={
          editingIndex === null || editingIndex === undefined
            ? null
            : questions[editingIndex]
        }
        onSave={handleSaveQuestion}
        readOnly={!permissions.edit}
      />

    </div>
  );
};

function getTypeLabel(type) {
  switch (type) {
    case "text":
      return "Text";
    case "textarea":
      return "Paragraph";
    case "number":
      return "Number";
    case "select":
      return "Multiple Choice (Single Selection)";
    case "multiselect":
      return "Multiple Choice (Multiple Selections)";
    case "boolean":
      return "Yes / No";
    case "date":
      return "Date";
    default:
      return type || "-";
  }
}

function ReferenceQuestionEditor({
  show,
  onClose,
  onSave,
  editingQuestion,
  readOnly = false,
}) {
  const [type, setType] = useState("text");
  const [question, setQuestion] = useState("");
  const [description, setDescription] = useState("");
  const [required, setRequired] = useState(true);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (editingQuestion) {
      setType(editingQuestion.rcq_type || "text");
      setQuestion(editingQuestion.rcq_question || "");
      setDescription(editingQuestion.rcq_description || "");
      setRequired(editingQuestion.rcq_required !== false);
      setOptions(Array.isArray(editingQuestion.rcq_options) ? editingQuestion.rcq_options : []);
    } else {
      setType("text");
      setQuestion("");
      setDescription("");
      setRequired(true);
      setOptions([]);
    }
  }, [editingQuestion, show]);

  const addOption = () => setOptions((prev) => [...prev, ""]);
  const updateOption = (index, value) => {
    setOptions((prev) =>
      prev.map((option, optionIndex) =>
        optionIndex === index ? value : option
      )
    );
  };
  const deleteOption = (index) => {
    setOptions((prev) => prev.filter((_, optionIndex) => optionIndex !== index));
  };

  const handleSave = () => {
    onSave({
      ...(editingQuestion || {}),
      rcq_question: question,
      rcq_description: description,
      rcq_type: type,
      rcq_required: required,
      rcq_options:
        type === "select" || type === "multiselect"
          ? options.map((option) => option.trim()).filter(Boolean)
          : [],
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingQuestion
            ? "Edit Reference Check Question"
            : "New Reference Check Question"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">What is the type of question?</label>
          <select
            className="form-select"
            value={type}
            disabled={readOnly}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="text">Text</option>
            <option value="textarea">Paragraph</option>
            <option value="number">Number</option>
            <option value="select">Multiple Choice (Single Selection)</option>
            <option value="multiselect">
              Multiple Choice (Multiple Selections)
            </option>
            <option value="boolean">Yes / No</option>
            <option value="date">Date</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Question</label>
          <textarea
            className="form-control"
            rows={2}
            value={question}
            disabled={readOnly}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <input
            className="form-control"
            value={description}
            disabled={readOnly}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Required</label>
          <select
            className="form-select"
            value={required ? "1" : "0"}
            disabled={readOnly}
            onChange={(e) => setRequired(e.target.value === "1")}
          >
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        </div>

        {(type === "select" || type === "multiselect") && (
          <div className="mb-3">
            <label className="form-label">Answers</label>
            <div className="list-group">
              {options.map((option, index) => (
                <div
                  key={`${index}-${option}`}
                  className="list-group-item d-flex align-items-center gap-2"
                >
                  <input
                    type="text"
                    className="form-control"
                    value={option}
                    disabled={readOnly}
                    onChange={(e) => updateOption(index, e.target.value)}
                  />
                  {!readOnly && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteOption(index)}
                    >
                      X
                    </button>
                  )}
                </div>
              ))}
            </div>
            {!readOnly && (
              <button
                className="btn btn-outline-secondary btn-sm mt-2"
                onClick={addOption}
              >
                + Add Answer
              </button>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {!readOnly && (
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ReferenceCheckAdminPage;
